import { useMemo, useState } from 'react';
import moment from '@g4/moment-utc';
import tracking from '@g4/g4-analytics';
import { useSSRQuery } from '../../../graphql';
import LOWFARE_FLIGHT_CALENDAR_QUERY from './lowFareFlightCalendar.query.graphql';
import { DEPARTING, RETURNING, tripTypeValues } from '../constants';
import useCachedSearchCriteria from './useCachedSearchCriteria';

const useLowFareCalendarQueries = (searchCriteria, tabDates) => ({
  [DEPARTING]: useLowFareQuery(
    searchCriteria,
    tabDates[DEPARTING]?.middle,
    DEPARTING
  ),
  [RETURNING]: useLowFareQuery(
    searchCriteria,
    tabDates[RETURNING]?.middle,
    RETURNING
  ),
});

const useLowFareQuery = (searchCriteria, focusDate, leg) => {
  const lowFareCalendarSearchCriteria = useLowFareSearchCriteria(
    searchCriteria,
    focusDate,
    leg
  );
  const dataLayerVars = tracking.getDataLayerVars();
  const googleAnalyticsClientId = dataLayerVars.customer_id;

  return useSSRQuery(LOWFARE_FLIGHT_CALENDAR_QUERY, {
    variables: lowFareCalendarSearchCriteria,
    skip:
      !lowFareCalendarSearchCriteria ||
      (leg !== DEPARTING &&
        searchCriteria.tripType !== tripTypeValues.ROUNDTRIP),
    context: {
      headers: {
        'google-analytics-client-id': googleAnalyticsClientId,
      },
    },
  });
};

const useLowFareSearchCriteria = (flightSearchCriteria, focusDate, leg) => {
  const dateRange = useCachedDateRange(focusDate);
  const dynamicCriteria = useMemo(
    () => createLowFareSearchCriteria(flightSearchCriteria, dateRange, leg),
    [flightSearchCriteria, dateRange, leg]
  );
  return useCachedSearchCriteria({
    lowFareCalendarSearchCriteria: dynamicCriteria,
  });
};

const useCachedDateRange = focusDate => {
  const initialRange = getRequiredDateRange(focusDate);
  const [cachedRanges, setCachedRanges] = useState({
    [initialRange.month]: initialRange.dateRange,
  });

  return useMemo(() => {
    const newRange = getRequiredDateRange(focusDate);
    const cachedRange = cachedRanges[newRange.month];
    if (cachedRange) {
      return cachedRange;
    }
    if (!cachedRanges[newRange.month]) {
      setCachedRanges(prev => ({
        ...prev,
        [newRange.month]: newRange.dateRange,
      }));
    }
    return newRange.dateRange;
  }, [focusDate, cachedRanges, setCachedRanges]);
};

const getRequiredDateRange = focus => {
  const currentMoment = moment();
  const middleDate = focus || currentMoment;

  const month = moment(middleDate).month();
  const start = moment(middleDate)
    .startOf('month')
    .subtract(3, 'days');
  const end = moment(middleDate)
    .endOf('month')
    .add(3, 'days');
  return {
    month,
    dateRange: {
      start: start.isBefore(currentMoment.format('YYYY-MM-DD'))
        ? currentMoment.format('YYYY-MM-DD')
        : start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
    },
  };
};

const createLowFareSearchCriteria = (flightSearchCriteria, dateRange, leg) => {
  const isDeparting = leg !== RETURNING;
  return {
    origin: isDeparting
      ? flightSearchCriteria.origin
      : flightSearchCriteria.destination,
    destination: isDeparting
      ? flightSearchCriteria.destination
      : flightSearchCriteria.origin,
    fareRuleType: flightSearchCriteria.tripType,
    adultsCount: flightSearchCriteria.adultsCount,
    childrenCount: flightSearchCriteria.childrenCount,
    infantCount: flightSearchCriteria.infantCount,
    searchStartDate: dateRange.start,
    searchEndDate: dateRange.end,
    utmSource: flightSearchCriteria.utmSource,
    utmMedium: flightSearchCriteria.utmMedium,
    utmCampaign: flightSearchCriteria.utmCampaign,
  };
};

export default useLowFareCalendarQueries;
